import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { State, Action, StateContext } from '@ngxs/store';
import { catchError, tap, finalize } from 'rxjs/operators';
import { Dealer, Seller } from '../../models/dealer.model';
import {
  UpdateActiveDealerAction,
  SetDealersAction,
  ResetDealerAction,
  GetSellersAction,
  CreateSellerAction,
  CreateSellerSuccessAction,
  CreateSellerFailedAction,
  SetDealersFailedAction,
  SellerSearchAction,
  SellerSearchSuccessAction,
  SellerSearchFailedAction,
  GetAllDealersAction,
  RemoveSellerFromDealerAction,
} from './dealer.actions';
import { DealerService } from '../../services/dealer.service';
import { NotificationService } from '../../services/notification.service';
import { SupportResetAction } from '@support/states/support/support.actions';
import { StateHelperService } from '@core/services/state-helper.service';
import { of, throwError } from 'rxjs';
import { LocalStorageService } from '@app/core/services/local-storage.service';

export interface DealerStateModel {
  active: Dealer;
  dealers: Dealer[];
  allDealers: Dealer[];
  isGettingDealers: boolean;
  isGettingAllDealers: boolean;
  isGettingSellers: boolean;
  sellers: Seller[];
  isCurrentlySearching: boolean;
  searchResults: Seller[];
}

const initialDealerState: DealerStateModel = {
  active: null,
  dealers: [],
  allDealers: [],
  isGettingDealers: false,
  isGettingAllDealers: false,
  isGettingSellers: false,
  sellers: [],
  isCurrentlySearching: false,
  searchResults: [],
};

enum Messages {
  SellerCreatedSuccess = 'Selger opprettet!',
  SellerCreatedFailed = 'Noe gikk galt ved opprettelse av selger.',
  GetSellersFailed = 'Noe gikk galt ved henting av selger.',
  GetDealersFailed = 'Noe gikk galt ved henting av dealere.',
  SellerSearchFailed = 'Noe gikk galt ved søk på selger.',
}

@State<DealerStateModel>({
  name: 'dealer',
  defaults: initialDealerState,
})
@Injectable()
export class DealerState {
  private notificationService = inject(NotificationService);
  private dealerService = inject(DealerService);
  private stateHelperService = inject(StateHelperService);
  private localStorageService = inject(LocalStorageService);

  @Action(SetDealersAction)
  setDealers(ctx: StateContext<DealerStateModel>, { dealers, initialDealerId }: SetDealersAction) {
    if (!dealers?.length) {
      return;
    }

    const targetDealerId =
      ctx.getState().active?.dealerId ?? this.localStorageService.getItem('activeDealerId') ?? initialDealerId;

    const active = dealers.find(dealer => dealer.dealerId === targetDealerId) ?? dealers[0];

    ctx.patchState({ dealers, active, isGettingDealers: false });
  }

  @Action(SetDealersFailedAction)
  setDealersFailed(ctx: StateContext<DealerStateModel>) {
    ctx.patchState({ isGettingDealers: false });

    this.notificationService.error(Messages.GetDealersFailed);
  }

  @Action(UpdateActiveDealerAction)
  updateActiveDealer(ctx: StateContext<DealerStateModel>, action: UpdateActiveDealerAction) {
    const dealerId = action.activeDealer.dealerId;
    this.localStorageService.setItem('activeDealerId', dealerId);

    ctx.patchState({ active: action.activeDealer });

    // reset everything that depend on what dealer you have
    this.stateHelperService.resetSaleState();

    ctx.dispatch([new SupportResetAction(), new GetSellersAction()]);

    this.notificationService.success(`Active Dealer: ${action.activeDealer.dealerName}`);
  }

  @Action(ResetDealerAction)
  resetDealerState({ setState }: StateContext<DealerStateModel>) {
    setState(initialDealerState);
  }

  @Action(GetSellersAction)
  getSellersAction(ctx: StateContext<DealerStateModel>) {
    ctx.patchState({ isGettingSellers: true });
    return this.dealerService.getSellers().pipe(
      tap((sellers: Seller[]) => ctx.patchState({ sellers })),
      catchError((error: HttpErrorResponse) => {
        this.notificationService.error(Messages.GetSellersFailed);
        return of(error);
      }),
      finalize(() => ctx.patchState({ isGettingSellers: false }))
    );
  }

  @Action(CreateSellerAction)
  createSellerAction(ctx: StateContext<DealerStateModel>, action: CreateSellerAction) {
    return this.dealerService.createSeller(action.seller).pipe(
      tap(() => ctx.dispatch(new CreateSellerSuccessAction())),
      catchError(() => ctx.dispatch(new CreateSellerFailedAction()))
    );
  }

  @Action(CreateSellerSuccessAction)
  createSellerSuccessAction(ctx: StateContext<DealerStateModel>) {
    this.notificationService.success(Messages.SellerCreatedSuccess);
    ctx.dispatch(new GetSellersAction());
  }

  @Action(CreateSellerFailedAction)
  createSellerFailedAction() {
    this.notificationService.error(Messages.SellerCreatedFailed);
  }

  @Action(SellerSearchAction)
  sellerSearchAction(ctx: StateContext<DealerStateModel>, { searchPhrase }: SellerSearchAction) {
    ctx.patchState({ isCurrentlySearching: true });
    return this.dealerService.sellerSearch(searchPhrase).pipe(
      tap((sellers: Seller[]) => ctx.dispatch(new SellerSearchSuccessAction(sellers))),
      catchError(() => ctx.dispatch(new SellerSearchFailedAction()))
    );
  }

  @Action(SellerSearchSuccessAction)
  sellerSearchSuccessAction(ctx: StateContext<DealerStateModel>, { sellers }: SellerSearchSuccessAction) {
    ctx.patchState({
      isCurrentlySearching: false,
      searchResults: sellers,
    });
  }

  @Action(SellerSearchFailedAction)
  sellerSearchFailedAction(ctx: StateContext<DealerStateModel>) {
    ctx.patchState({ isCurrentlySearching: false });
    this.notificationService.error(Messages.SellerSearchFailed);
  }

  @Action(GetAllDealersAction)
  getAllDealersAction({ patchState, getState }: StateContext<DealerStateModel>) {
    if (getState().allDealers.length > 0) {
      return;
    }

    patchState({ isGettingAllDealers: true });

    return this.dealerService.getAllDealers().pipe(
      tap(allDealers => patchState({ allDealers })),
      catchError(error => {
        this.notificationService.error(Messages.GetDealersFailed);
        return throwError(error);
      }),
      finalize(() => patchState({ isGettingAllDealers: false }))
    );
  }

  @Action(RemoveSellerFromDealerAction)
  removeSellerFromDealer({ dispatch }, { seller }: RemoveSellerFromDealerAction) {
    return this.dealerService.removeSellerFromDealer(seller.userId).pipe(
      tap(() => {
        this.notificationService.success(`Selger ${seller.fullName} ble fjernet`);
        dispatch(new GetSellersAction());
      })
    );
  }
}
